import { useTranslation } from 'react-i18next'
import { CheckCircle2, Circle } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'

const BLOCK_TYPES: Record<string, string> = {
  title: 'Titel',
  bullet_points: 'Bullet Points',
  description: 'Beschreibung',
  keywords: 'Keywords',
  a_plus: 'A+ Content',
}

interface ContentBlock {
  id: string
  block_type: string
  current_version?: { content?: unknown }
}

interface ContentStatusCardProps {
  contentBlocks: ContentBlock[]
  onEditContent: () => void
}

function hasContent(block: ContentBlock | undefined): boolean {
  return !!block?.current_version?.content
}

export function ContentStatusCard({ contentBlocks, onEditContent }: ContentStatusCardProps) {
  const { t } = useTranslation()

  const blockMap = new Map(contentBlocks.map((b) => [b.block_type, b]))

  return (
    <Card className="product-overview-content-status">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('products.overview.contentStatus')}</CardTitle>
        <button
          id={EID['product-overview-edit-content-link']}
          onClick={onEditContent}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          {t('products.overview.editContent')} &rarr;
        </button>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {Object.entries(BLOCK_TYPES).map(([key, label]) => {
            const filled = hasContent(blockMap.get(key))
            return (
              <li key={key}>
                <div className="flex items-center gap-2.5">
                  {filled ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                  ) : (
                    <Circle className="h-4 w-4 shrink-0 text-neutral-300 dark:text-neutral-600" />
                  )}
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{label}</span>
                  {!filled && (
                    <span className="ml-auto text-xs text-neutral-400 dark:text-neutral-500">
                      {t('products.overview.notCreated')}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 ml-6.5 h-1 rounded-full bg-neutral-100 dark:bg-neutral-700">
                  <div
                    className={`h-1 rounded-full transition-all ${filled ? 'w-full bg-green-500' : 'w-0'}`}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}
